import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Modal } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { login, changePassword } from '../../services/auth';
import { LoginResponse } from '../../types';
import { antdMessage } from '../../utils/antdGlobal';
import { getPublicConfigValue } from '../../services/config';
import './style.css';

/**
 * 管理员登录页（内部入口）
 * 与查询页同属「学府印信」公文体系：印章标识、双线规、竖排刊记
 */
const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [loginFormData, setLoginFormData] = useState<any>(null);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [changePasswordForm] = Form.useForm();
  const [systemTitle, setSystemTitle] = useState('成绩查询验证系统');
  const [captchaAppId, setCaptchaAppId] = useState('');
  const navigate = useNavigate();

  // 获取系统配置（标题 + 验证码 AppId）
  useEffect(() => {
    const fetchSystemConfig = async () => {
      try {
        const titleResult = await getPublicConfigValue('system_title');
        if (titleResult.value) {
          setSystemTitle(titleResult.value);
          document.title = titleResult.value;
        }
      } catch {
        // 使用默认值
      }
      try {
        const captchaResult = await getPublicConfigValue('captcha_app_id');
        if (captchaResult.value) {
          setCaptchaAppId(captchaResult.value);
        }
      } catch {
        // 使用默认值
      }
    };
    fetchSystemConfig();
  }, []);

  // 检查是否已登录（通过 API 验证 token 有效性，防止过期 token 导致重定向循环）
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    if (!userInfo.role) return;

    // 调用后端验证 token 有效性，避免使用过期 token 导致无限重定向
    import('../../services/auth').then(({ getCurrentUser }) => {
      getCurrentUser()
        .then(() => {
          // token 有效，执行跳转
          if (userInfo.role === 'super_admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/campus/dashboard');
          }
        })
        .catch(() => {
          // token 无效/过期，清除 localStorage，留在登录页
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          localStorage.removeItem('campusInfo');
        });
    });
  }, [navigate]);

  /**
   * 登录表单提交：先弹出腾讯图形验证码，验证通过后再发起登录请求
   */
  const handleLoginSubmit = async (values: any) => {
    // 优先使用从后端获取的 AppId，回退到硬编码默认值
    const appId = captchaAppId || '194987062';

    // 检查 TencentCaptcha SDK 是否已加载
    if (typeof (window as any).TencentCaptcha === 'undefined') {
      antdMessage.error('验证码组件加载失败，请刷新页面后重试');
      return;
    }

    const captcha = new (window as any).TencentCaptcha(
      appId,
      async (res: { ret: number; ticket: string; randstr: string; appid?: string }) => {
        if (res.ret !== 0) {
          // 用户关闭验证码或出错，不做任何处理
          return;
        }
        // 验证卡片弹出成功，执行登录
        setLoading(true);
        try {
          const response: LoginResponse = await login(
            values.account,
            values.password,
            res.ticket,
            res.randstr,
          );

          // 保存登录信息
          localStorage.setItem('token', response.token);
          localStorage.setItem('userInfo', JSON.stringify(response.adminInfo));
          if (response.campusInfo) {
            localStorage.setItem('campusInfo', JSON.stringify(response.campusInfo));
          }

          antdMessage.success('登录成功');

          // 判断是否需要修改密码
          if (response.adminInfo.forceChangePassword) {
            setLoginFormData(values);
            setChangePasswordVisible(true);
          } else {
            if (response.adminInfo.role === 'super_admin') {
              navigate('/admin/dashboard');
            } else {
              navigate('/campus/dashboard');
            }
          }
        } catch (error) {
          antdMessage.error('登录失败，请检查账号密码');
        } finally {
          setLoading(false);
        }
      },
      { bizState: 'admin-login' },
    );
    captcha.show();
  };

  /**
   * 首次登录修改密码，成功后以新密码自动重新登录
   */
  const handleChangePassword = async (values: any) => {
    try {
      await changePassword(loginFormData.password, values.newPassword);
      antdMessage.success('密码修改成功，请重新登录');
      setChangePasswordVisible(false);

      // 清除登录信息
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('campusInfo');

      // 重新登录
      const response: LoginResponse = await login(loginFormData.account, values.newPassword);
      localStorage.setItem('token', response.token);
      localStorage.setItem('userInfo', JSON.stringify(response.adminInfo));
      if (response.campusInfo) {
        localStorage.setItem('campusInfo', JSON.stringify(response.campusInfo));
      }

      // 跳转
      if (response.adminInfo.role === 'super_admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/campus/dashboard');
      }
    } catch (error) {
      antdMessage.error('密码修改失败');
    }
  };

  return (
    <div className="login-page">
      {/* 两侧竖排刊记（宽屏显示） */}
      <aside className="lg-side-note lg-side-left" aria-hidden="true">
        <span>成绩查询验证系统</span>
        <i />
        <span>内部管理入口</span>
      </aside>
      <aside className="lg-side-note lg-side-right" aria-hidden="true">
        <span>未经授权</span>
        <i />
        <span>禁止入内</span>
      </aside>

      {/* ===== 登录公文纸 ===== */}
      <section className="lg-document">
        <span className="lg-corner lg-corner-tl" />
        <span className="lg-corner lg-corner-tr" />
        <span className="lg-corner lg-corner-bl" />
        <span className="lg-corner lg-corner-br" />
        {/* 印章水印 */}
        <span className="lg-watermark" aria-hidden="true">验</span>

        <header className="lg-header">
          <span className="lg-seal">验</span>
          <h1 className="lg-title">{systemTitle}</h1>
          <p className="lg-subtitle">STAFF ACCESS · 管理入口</p>
        </header>

        <Form
          name="login"
          size="large"
          onFinish={handleLoginSubmit}
          autoComplete="off"
          className="lg-form"
        >
          <Form.Item
            name="account"
            rules={[{ required: true, message: '请输入账号' }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#85929a' }} />}
              placeholder="请输入账号"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#85929a' }} />}
              placeholder="请输入密码"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
              className="lg-submit"
            >
              登　录
            </Button>
          </Form.Item>
        </Form>

        <footer className="lg-footer">
          <p>首次登录请修改初始密码 · 仅限授权管理人员使用</p>
        </footer>
      </section>

      <p className="lg-copyright">© {new Date().getFullYear()} {systemTitle} · 保留所有权利</p>

      {/* 修改密码弹窗 */}
      <Modal
        title="首次登录，请修改密码"
        open={changePasswordVisible}
        closable={false}
        mask={{ closable: false }}
        footer={null}
      >
        <Form
          form={changePasswordForm}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '密码至少8位' },
              {
                pattern: /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).*$/,
                message: '密码必须包含字母、数字和特殊符号',
              },
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请确认密码" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              确认修改
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Login;
